import { isUsingNewComponents } from "../../add-element/utils";
import { isNewLayoutsEnabled } from "../../contexts/StaticSiteDataContext";
import { useCurrentSite, useSites } from "../../contexts/models/SiteContext";
import { openModal } from "../../modals/actions";
import photoActions from "../../photos/actions";
import { openMediaSelectionModal } from "../../photos/utils";
import { MediaDisplay, Photo, WeddingSite } from "../../resources/modelTypes";
import { RootState } from "../../resources/reduxTypes";
import { saveHeaderImageSlot } from "../../website/components/EditLayout/actions";
import InnerNodes from "../../website/components/InnerNodes";
import { HeaderMediaEditProvider } from "../../website/components/header/media/HeaderMediaEditContext";
import { getPrimaryHeaderSlotPosition } from "../../website/components/header/media/getPrimaryHeaderImageUrl";
import EditableImageAsset from "./EditableImageAsset";
import { Global } from "@emotion/react";
import elementResizeDetectorMaker from "element-resize-detector";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import TopBarProgress from "react-topbar-progress-indicator";

let erd;

if (typeof window !== "undefined") {
  erd = elementResizeDetectorMaker({
    strategy: "scroll",
  });
}

const LEGACY_IMAGE_SELECTOR = "img";
const MODULAR_IMAGE_SELECTOR =
  'img[data-cy="mainImage"], img[data-cy="leftImage"], img[data-cy="rightImage"]';

export const assetSpecs = {
  l1: {
    themeImage: {
      algorithm: "crop" as const,
      height: 1300,
      key: "theme_image",
      width: 2400,
    },
  },
  l2: {
    brideImage: {
      algorithm: "crop" as const,
      height: 1000,
      key: "bride_image",
      width: 1000,
    },
    groomImage: {
      algorithm: "crop" as const,
      height: 1000,
      key: "groom_image",
      width: 1000,
    },
  },
  l3: {
    themeImage: {
      algorithm: "crop" as const,
      height: 2134,
      key: "theme_image",
      width: 3200,
    },
  },
};

/** Crop specs for modular media slots (legacy l1/l2/l3 dimensions). */
const modularMediaSpecs = {
  fullBleed: {
    ...assetSpecs.l3.themeImage,
    key: "header_slot_0",
    slotIndex: 0,
  },
  landscape: {
    ...assetSpecs.l1.themeImage,
    key: "header_slot_0",
    slotIndex: 0,
  },
  // Portrait slots for the overlapping collage (3:4).
  overlapping: [
    {
      algorithm: "crop" as const,
      height: 1200,
      key: "header_slot_0",
      slotIndex: 0,
      width: 900,
    },
    {
      algorithm: "crop" as const,
      height: 1200,
      key: "header_slot_1",
      slotIndex: 1,
      width: 900,
    },
  ],
  // Natural-aspect pair (same height, variable widths) — landscape crop box.
  sideBySide: [
    {
      ...assetSpecs.l1.themeImage,
      key: "header_slot_0",
      slotIndex: 0,
    },
    {
      ...assetSpecs.l1.themeImage,
      key: "header_slot_1",
      slotIndex: 1,
    },
  ],
  square: [
    {
      ...assetSpecs.l2.brideImage,
      key: "header_slot_0",
      slotIndex: 0,
    },
    {
      ...assetSpecs.l2.groomImage,
      key: "header_slot_1",
      slotIndex: 1,
    },
  ],
} as const;

/** Crop box for a modular header slot at the given position. */
const modularSlotAssetSpec = (
  slotIndex: number,
  cropSpec: Pick<AssetSpec, "algorithm" | "height" | "width">,
): AssetSpec => ({
  ...cropSpec,
  key: `header_slot_${slotIndex}`,
  slotIndex,
});

export type AssetSpec = {
  algorithm: "crop";
  height: number;
  key: string;
  slotIndex?: number;
  width: number;
};

type AssetPathKey =
  | "bride_image"
  | "groom_image"
  | "theme_image"
  | "header_slot_0"
  | "header_slot_1";

type Position = {
  left: number;
  top: number;
};

type LayoutProps = {
  onEdit: (assetSpec: AssetSpec) => void;
  positions: Position[];
};

const Layout1 = ({ onEdit, positions }: LayoutProps) => (
  <div>
    <EditableImageAsset
      assetSpec={assetSpecs.l1.themeImage}
      onEdit={onEdit}
      position={positions[0]}
    />
  </div>
);

const Layout2 = ({ onEdit, positions }: LayoutProps) => (
  <div>
    <EditableImageAsset
      assetSpec={assetSpecs.l2.brideImage}
      onEdit={onEdit}
      position={positions[0]}
    />
    <EditableImageAsset
      assetSpec={assetSpecs.l2.groomImage}
      onEdit={onEdit}
      position={positions[1]}
    />
  </div>
);

const assetPaths = {
  bride_image: {
    "div:nth-of-type(3) div:first-of-type div:first-of-type div:first-of-type div:first-of-type img":
      {
        opacity: 0.5,
      },
  },
  groom_image: {
    "div:nth-of-type(3) div:first-of-type div:first-of-type div:first-of-type div:nth-of-type(2) img":
      {
        opacity: 0.5,
      },
  },
  header_slot_0: {
    'img[data-cy="mainImage"], img[data-cy="leftImage"]': {
      opacity: 0.5,
    },
  },
  header_slot_1: {
    'img[data-cy="rightImage"]': {
      opacity: 0.5,
    },
  },
  theme_image: {
    img: {
      opacity: 0.5,
    },
  },
};

const Layout3 = ({ onEdit, positions }: LayoutProps) => (
  <div>
    <EditableImageAsset
      assetSpec={assetSpecs.l3.themeImage}
      onEdit={onEdit}
      position={positions[0]}
    />
  </div>
);

export type EditableHeaderProps = {
  children: ReactNode;
};

const EditableHeader = ({ children }: EditableHeaderProps) => {
  const site = useCurrentSite();
  const { replaceItem, setSiteAsset } = useSites();
  const layout = site.themeIdentifier;
  const newLayoutsEnabled = isNewLayoutsEnabled(site);
  const mediaDisplay =
    (site as WeddingSite).siteDetails?.mediaDisplay ?? MediaDisplay.Landscape;
  const photosById = useSelector(
    (state: RootState) => state.resources.photos.byId as Record<string, Photo>,
  );
  const dispatch = useDispatch();
  const openModalFn = useCallback(
    (name: string, modalProps: Record<string, unknown>) =>
      dispatch(openModal(name, modalProps)),
    [dispatch],
  );
  const [assetTransitioning, setAssetTransitioning] = useState<string | null>(
    null,
  );
  const [positions, setPositions] = useState<{ top: number; left: number }[]>(
    [],
  );
  const [nodes, setNodes] = useState<Element[]>([]);
  const [layoutSnapshot, setLayoutSnapshot] = useState(layout);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  if (layoutSnapshot !== layout) {
    setLayoutSnapshot(layout);
    setNodes([]);
    setPositions([]);
  }

  const selectors = useMemo(
    () => ({
      images: newLayoutsEnabled
        ? MODULAR_IMAGE_SELECTOR
        : LEGACY_IMAGE_SELECTOR,
    }),
    [newLayoutsEnabled],
  );

  const setAsset = (assetSpec: AssetSpec, selectedImage: string) => {
    setAssetTransitioning(assetSpec.key);
    setSiteAsset(assetSpec.key, selectedImage).finally(() => {
      setAssetTransitioning(null);
    });
  };

  const saveModularHeaderSlot = useCallback(
    (assetSpec: AssetSpec, selectedImage: string, photoPk: number) => {
      const slotIndex = assetSpec.slotIndex ?? 0;
      const weddingSite = site as WeddingSite;

      setAssetTransitioning(assetSpec.key);
      void saveHeaderImageSlot(
        weddingSite.headerImages ?? [],
        slotIndex,
        photoPk,
        selectedImage,
        (photoUuid) => photosById[photoUuid]?.pk,
      )
        .then((nextHeaderImages) => {
          replaceItem({
            ...site,
            headerImages: nextHeaderImages,
          });
        })
        .finally(() => {
          setAssetTransitioning(null);
        });
    },
    [photosById, replaceItem, site],
  );

  const saveModularHeaderSlotRef = useRef(saveModularHeaderSlot);

  useEffect(() => {
    saveModularHeaderSlotRef.current = saveModularHeaderSlot;
  }, [saveModularHeaderSlot]);

  const openModularHeaderPhotoManager = useCallback(
    (assetSpec: AssetSpec) => {
      openModalFn("HEADER_PHOTO_MANAGER_MODAL", {
        cropAspectRatio: assetSpec.width / assetSpec.height,
        minSize: {
          height: assetSpec.height,
          width: assetSpec.width,
        },
        onSave: (selectedImage: string, photoPk: number) =>
          saveModularHeaderSlotRef.current(assetSpec, selectedImage, photoPk),
      });
    },
    [openModalFn],
  );

  useEffect(() => {
    if (newLayoutsEnabled) {
      dispatch(photoActions.fetch());
    }
  }, [dispatch, newLayoutsEnabled]);

  const onEdit = (assetSpec: AssetSpec) => {
    if (newLayoutsEnabled) {
      openModularHeaderPhotoManager(assetSpec);
      return;
    }

    if (isUsingNewComponents(site)) {
      openModalFn("HEADER_PHOTO_MANAGER_MODAL", {
        cropAspectRatio: assetSpec.width / assetSpec.height,
        minSize: {
          height: assetSpec.height,
          width: assetSpec.width,
        },
        onSave: (selectedImage: string) => setAsset(assetSpec, selectedImage),
      });
    } else {
      openMediaSelectionModal(
        openModalFn,
        (selectedImage) => setAsset(assetSpec, selectedImage),
        assetSpec.width / assetSpec.height,
        {
          height: assetSpec.height,
          width: assetSpec.width,
        },
      );
    }
  };

  const onEditSlot = useCallback(
    (slotIndex: number) => {
      openModularHeaderPhotoManager(
        modularSlotAssetSpec(slotIndex, assetSpecs.l1.themeImage),
      );
    },
    [openModularHeaderPhotoManager],
  );

  const headerMediaEditValue = useMemo(
    () => (newLayoutsEnabled ? { onEditSlot } : null),
    [newLayoutsEnabled, onEditSlot],
  );

  const primaryHeaderSlot = useMemo(
    () =>
      getPrimaryHeaderSlotPosition((site as WeddingSite).headerImages ?? []),
    [site],
  );

  const landscapeSlotAssetSpec = useMemo(
    () => modularSlotAssetSpec(primaryHeaderSlot, assetSpecs.l1.themeImage),
    [primaryHeaderSlot],
  );

  const fullBleedSlotAssetSpec = useMemo(
    () => modularSlotAssetSpec(primaryHeaderSlot, assetSpecs.l3.themeImage),
    [primaryHeaderSlot],
  );

  const updatePositions = useCallback(() => {
    const wrapperNode: Element | null = wrapperRef.current;
    if (!wrapperNode) {
      return;
    }

    const wrapperRect = wrapperNode.getBoundingClientRect();

    const newPositions = nodes.map((node: Element) => {
      const rect = node.getBoundingClientRect();

      // Clamp to the wrapper's visible box so overflowed media (e.g. SideBySide
      // with width:auto images wider than the viewport) still gets on-screen
      // pencils at the visible top-right of each image.
      const visibleRight = Math.min(rect.right, wrapperRect.right);
      const visibleTop = Math.max(rect.top, wrapperRect.top);

      return {
        left: visibleRight - wrapperRect.left,
        top: visibleTop - wrapperRect.top,
      };
    });

    setPositions(newPositions);
  }, [nodes]);

  useEffect(() => {
    if (nodes.length === 0) {
      return undefined;
    }

    updatePositions();

    // width:auto images (SideBySide / Marquee) report 0 width until load.
    const onImageLoad = () => updatePositions();
    nodes.forEach((node) => {
      if (node instanceof HTMLImageElement && !node.complete) {
        node.addEventListener("load", onImageLoad);
      }
    });

    // Update positions when the container changes size
    const wrapperNode = wrapperRef.current;

    if (erd !== undefined) {
      erd.listenTo(wrapperNode, () => updatePositions());
    }

    return () => {
      nodes.forEach((node) => {
        if (node instanceof HTMLImageElement) {
          node.removeEventListener("load", onImageLoad);
        }
      });
      if (erd !== undefined) {
        erd.uninstall(wrapperNode);
      }
    };
  }, [nodes, updatePositions]);

  const onNodesChanged = useCallback((result) => {
    if (result === null) {
      setNodes([]);
      setPositions([]);
    } else {
      setNodes([...result["images"].values()]);
    }
  }, []);

  let controls: React.ReactElement | null = null;

  if (positions.length > 0) {
    if (newLayoutsEnabled) {
      switch (mediaDisplay) {
        case MediaDisplay.None:
          break;
        case MediaDisplay.Square:
          controls = (
            <div>
              <EditableImageAsset
                assetSpec={modularMediaSpecs.square[0]}
                onEdit={onEdit}
                position={positions[0]}
              />
              <EditableImageAsset
                assetSpec={modularMediaSpecs.square[1]}
                onEdit={onEdit}
                position={positions[1]}
              />
            </div>
          );
          break;
        case MediaDisplay.SideBySide:
          controls = (
            <div>
              <EditableImageAsset
                assetSpec={modularMediaSpecs.sideBySide[0]}
                onEdit={onEdit}
                position={positions[0]}
              />
              <EditableImageAsset
                assetSpec={modularMediaSpecs.sideBySide[1]}
                onEdit={onEdit}
                position={positions[1]}
              />
            </div>
          );
          break;
        case MediaDisplay.Overlapping:
          controls = (
            <div>
              <EditableImageAsset
                assetSpec={modularMediaSpecs.overlapping[0]}
                onEdit={onEdit}
                position={positions[0]}
              />
              <EditableImageAsset
                assetSpec={modularMediaSpecs.overlapping[1]}
                onEdit={onEdit}
                position={positions[1]}
              />
            </div>
          );
          break;
        case MediaDisplay.FullBleed:
          controls = (
            <div>
              <EditableImageAsset
                assetSpec={fullBleedSlotAssetSpec}
                onEdit={onEdit}
                position={positions[0]}
              />
            </div>
          );
          break;
        case MediaDisplay.Marquee:
        case MediaDisplay.Slideshow:
          // Pencils are rendered on each frame/slide inside the media
          // component (via HeaderMediaEditContext) so they travel with
          // the animated track.
          break;
        case MediaDisplay.Landscape:
        default:
          controls = (
            <div>
              <EditableImageAsset
                assetSpec={landscapeSlotAssetSpec}
                onEdit={onEdit}
                position={positions[0]}
              />
            </div>
          );
          break;
      }
    } else {
      switch (layout) {
        case "l1":
          if (site.isWedding) {
            controls = <Layout1 onEdit={onEdit} positions={positions} />;
          }
          break;
        case "l2":
          controls = <Layout2 onEdit={onEdit} positions={positions} />;
          break;
        case "l3":
          controls = <Layout3 onEdit={onEdit} positions={positions} />;
          break;
        case "l4":
          break; // no controls necessary
      }
    }
  }

  const assetStyle = useMemo(() => {
    if (assetTransitioning === null) {
      return {};
    }

    const slotMatch = /^header_slot_(\d+)$/.exec(assetTransitioning);
    const isPositionKeyedMedia =
      newLayoutsEnabled &&
      (mediaDisplay === MediaDisplay.Marquee ||
        mediaDisplay === MediaDisplay.Slideshow ||
        mediaDisplay === MediaDisplay.Landscape ||
        mediaDisplay === MediaDisplay.FullBleed);

    // These treatments key slots by header position on data-slot-index.
    if (slotMatch && isPositionKeyedMedia) {
      return {
        [`img[data-slot-index="${slotMatch[1]}"]`]: {
          opacity: 0.5,
        },
      };
    }

    if (assetTransitioning in assetPaths) {
      return assetPaths[assetTransitioning as AssetPathKey];
    }

    // Other N-slot modular media (future layouts) dim by data-slot-index.
    if (slotMatch) {
      return {
        [`img[data-slot-index="${slotMatch[1]}"]`]: {
          opacity: 0.5,
        },
      };
    }

    return {};
  }, [assetTransitioning, mediaDisplay, newLayoutsEnabled]);

  // Remount when the modular media treatment changes so InnerNodes re-queries
  // img[data-cy] nodes. Context updates re-render WebsiteHeader without
  // changing EditableHeader's children element identity, which would otherwise
  // leave stale detached image refs and missing pencils.
  const innerNodesKey = newLayoutsEnabled ? mediaDisplay : layout;

  return (
    <div
      className="customize-header"
      css={{ position: "relative" }}
      ref={wrapperRef}
    >
      <Global
        styles={{
          ".customize-header": assetStyle,
        }}
      />
      {assetTransitioning ? <TopBarProgress /> : null}
      <HeaderMediaEditProvider value={headerMediaEditValue}>
        <InnerNodes
          callback={onNodesChanged}
          key={innerNodesKey}
          selectAll
          selectors={selectors}
        >
          {children}
        </InnerNodes>
      </HeaderMediaEditProvider>
      {controls}
    </div>
  );
};

export default EditableHeader;
